import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useMemo } from 'react';
import { getDayTypeDotColors } from './cUtils';
import { Table, TableBody, TableCell, TableHead, TableRow, Box, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import { getMonthMatrix, MAX_VISIBLE_ITEMS } from './cUtils';
import { CalendarItemChip } from './components/CalendarItemChip';
import { CalendarFootballEvent } from './components/CalendarFootballEvent';
import { CalendarMobileEventsList } from './components/CalendarMobileEventsList';
import { CalendarMoreButton } from './components/CalendarMoreButton';
import { centered } from '@ui/style/style';
import { useTranslation } from 'react-i18next';
export const MonthView = ({ year, month, today, items, handleOpenModal, isSponsorshipDetailsCalendarTab = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const { t } = useTranslation();
    const weekDays = useMemo(() => [
        t('common.monday'),
        t('common.tuesday'),
        t('common.wednesday'),
        t('common.thursday'),
        t('common.friday'),
        t('common.saturday'),
        t('common.sunday')
    ], [t]);
    const matrix = getMonthMatrix(year, month);
    const [expandedCells, setExpandedCells] = useState({});
    const [selectedMobileDate, setSelectedMobileDate] = useState(null);
    const getCellKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    const handleExpand = (date) => {
        setExpandedCells((prev) => ({
            ...prev,
            [getCellKey(date)]: true
        }));
    };
    const handleMobileCellClick = (date, dayItems) => {
        if (isMobile) {
            setSelectedMobileDate(selectedMobileDate?.getTime() === date.getTime() ? null : date);
        }
    };
    React.useEffect(() => {
        setSelectedMobileDate(null);
    }, [year, month]);
    return (_jsxs(Box, { children: [_jsx(Box, { sx: {
                    maxHeight: isMobile
                        ? 'auto'
                        : isSponsorshipDetailsCalendarTab
                            ? 'calc(100vh - 320px)'
                            : 'calc(100vh - 360px)',
                    pt: 0,
                    borderRadius: 3,
                    overflowY: 'auto'
                }, children: _jsx(Box, { sx: { overflowX: 'auto', width: '100%' }, children: _jsxs(Table, { sx: {
                            minWidth: isMobile ? '100%' : 900,
                            bgcolor: theme.palette.background.paper,
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
                            backdropFilter: 'blur(20px)',
                            '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                                // borderLeft: "1px solid #f0f0f0 !important",
                                borderRight: isMobile
                                    ? undefined
                                    : `1px solid ${alpha(theme.palette.grey[500], 0.2)}`
                            }
                        }, children: [_jsx(TableHead, { children: _jsx(TableRow, { style: {
                                        border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 10,
                                        background: alpha(theme.palette.primary.main, 0.08)
                                    }, children: weekDays.map((day) => (_jsx(TableCell, { sx: {
                                            height: isMobile ? 50 : 68,
                                            verticalAlign: 'middle',
                                            minWidth: isMobile ? 'auto' : 145,
                                            py: isMobile ? 0 : theme.spacing(0.5),
                                            color: theme.palette.text.primary,
                                            fontWeight: 400,
                                            fontSize: 12,
                                            border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                                            background: alpha(theme.palette.primary.main, 0.08),
                                            textAlign: 'center',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 11,
                                            px: isMobile ? 0.5 : theme.spacing(0.75)
                                        }, children: isMobile ? day.slice(0, 3) : day }, day))) }) }), _jsx(TableBody, { children: matrix.map((week, i) => (_jsx(TableRow, { children: week.map((date, j) => {
                                        const isToday = date &&
                                            date.getDate() === today.getDate() &&
                                            date.getMonth() === today.getMonth() &&
                                            date.getFullYear() === today.getFullYear();
                                        const isCurrentMonth = date && date.getMonth() === month;
                                        const dayItems = date
                                            ? items.filter((item) => {
                                                if (['event'].includes(item.type)) {
                                                    return (date >=
                                                        new Date(item.start.getFullYear(), item.start.getMonth(), item.start.getDate()) &&
                                                        date <=
                                                            new Date(item.end.getFullYear(), item.end.getMonth(), item.end.getDate()));
                                                }
                                                return item.start.toDateString() === date.toDateString();
                                            })
                                            : [];
                                        const cellKey = date ? getCellKey(date) : '';
                                        const expanded = date ? expandedCells[cellKey] : false;
                                        const visibleItems = expanded ? dayItems : dayItems.slice(0, MAX_VISIBLE_ITEMS);
                                        const hiddenCount = dayItems.length - MAX_VISIBLE_ITEMS;
                                        const hasEvents = dayItems.length > 0;
                                        const isWeekend = j === 5 || j === 6;
                                        return (_jsx(TableCell, { sx: {
                                                height: isMobile ? 50 : 68,
                                                minWidth: isMobile ? 40 : 145,
                                                maxWidth: isMobile ? 40 : 145,
                                                verticalAlign: 'top',
                                                bgcolor: isWeekend ? '#F4F6F8' : 'transparent',
                                                color: isCurrentMonth ? theme.palette.text.primary : '#b3b3b3',
                                                textAlign: 'right',
                                                px: isMobile ? 0.5 : theme.spacing(0.75),
                                                py: isMobile ? 0.5 : theme.spacing(0.5),
                                                position: 'relative',
                                                zIndex: 1,
                                                border: isMobile
                                                    ? 'none'
                                                    : `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                                                cursor: isMobile ? 'pointer' : 'default'
                                            }, onClick: () => date && handleMobileCellClick(date, dayItems), children: date && (_jsxs(Box, { sx: { position: 'relative', zIndex: 1 }, children: [_jsxs(Box, { sx: {
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: isMobile ? 'center' : 'flex-end',
                                                            flexDirection: isMobile ? 'column' : 'row',
                                                            mb: isMobile ? 0 : 0.5
                                                        }, children: [isMobile &&
                                                                selectedMobileDate &&
                                                                date &&
                                                                selectedMobileDate.getTime() === date.getTime() ? (_jsx(Box, { sx: {
                                                                    backgroundColor: 'primary.darker',
                                                                    color: '#fff',
                                                                    borderRadius: '50%',
                                                                    width: 28,
                                                                    height: 28,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    boxShadow: '0 2px 8px 0 rgba(56,189,248,0.15)'
                                                                }, children: _jsx(Typography, { variant: 'caption', fontWeight: 400, component: 'span', sx: {
                                                                        lineHeight: 1,
                                                                        color: '#fff'
                                                                    }, children: date.getDate() }) })) : (_jsx(Box, { sx: {
                                                                    backgroundColor: isToday ? 'primary.darker' : 'transparent',
                                                                    color: '#fff',
                                                                    borderRadius: '50%',
                                                                    width: 20,
                                                                    height: 20,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }, children: _jsx(Typography, { variant: 'caption', fontWeight: 400, color: isToday
                                                                        ? '#fff'
                                                                        : isCurrentMonth
                                                                            ? theme.palette.text.primary
                                                                            : '#b3b3b3', component: 'span', children: date.getDate() }) })), isMobile && (_jsx(Box, { sx: {
                                                                    ...centered,
                                                                    flexDirection: 'row',
                                                                    mt: 0.5
                                                                }, children: getDayTypeDotColors(dayItems, !!(selectedMobileDate &&
                                                                    date &&
                                                                    selectedMobileDate.getTime() === date.getTime()), theme).map((color, idx) => (_jsx(Box, { sx: {
                                                                        width: 4,
                                                                        height: 4,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: color
                                                                    } }, idx))) }))] }), !isMobile && (_jsxs(_Fragment, { children: [visibleItems.map((item) => {
                                                                if (item.type === 'event' &&
                                                                    item.logo &&
                                                                    item.teamName) {
                                                                    return (_jsx(CalendarFootballEvent, { logo: item.logo, teamName: item.teamName }, item.id));
                                                                }
                                                                return (_jsx(CalendarItemChip, { item: item, 
                                                                    // onClick={() => handleOpenModal && handleOpenModal(item)}
                                                                    isSponsorshipDetailsCalendarTab: isSponsorshipDetailsCalendarTab }, item.id));
                                                            }), !expanded && hiddenCount > 0 && (_jsx(CalendarMoreButton, { count: hiddenCount, onClick: (e) => {
                                                                    e.stopPropagation();
                                                                    if (date && handleOpenModal) {
                                                                        window.calendarDialogItems = dayItems;
                                                                        window.calendarDialogSelectedDate = date;
                                                                        window.calendarDialogAllItems = items;
                                                                        handleOpenModal({ ...dayItems[0], start: date });
                                                                    }
                                                                } }))] }))] })) }, j));
                                    }) }, i))) })] }) }) }), isMobile && (_jsx(CalendarMobileEventsList, { selectedDate: selectedMobileDate, items: items, handleOpenModal: handleOpenModal, isSponsorshipDetailsCalendarTab: isSponsorshipDetailsCalendarTab }))] }));
};
